export const SETTINGS_TABS = [
  "organisation",
  /**
   * Redesign 2026-09 (pass 01 board 1f): Members & roles left the sidebar and became two Settings
   * tabs, unconditionally — never hidden until a second member exists, because a menu that changes
   * shape with row counts is unfindable the second time and "invite a teammate" is exactly the path
   * that must stay visible. They are two tabs rather than one because they answer different
   * questions: who may sign in, and what a role allows.
   */
  "members",
  "roles",
  "subscription",
  /**
   * ADR-0017 addendum of 2026-10-01 (second): every module that has a workspace, held or not, with its
   * price, its state and its one action — where the switcher's "Add modules…" lands. After Subscription,
   * because it is the same question asked product by product.
   */
  "modules",
  /**
   * M20 (ADR-0024, contracts §11): the organisation's shared SMS balance — CORE, not an add-on, so it is a
   * Settings section rather than a workspace entry. After Modules: the third money question, asked of the
   * one wallet every module that texts somebody draws on. Gated `sms:view`; the recharge needs `billing:manage`.
   */
  "sms",
  "webhooks",
  "api-keys",
  "audit",
  "danger",
] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];

/**
 * M11 replaced "Plan & usage" and "Billing" with one Subscription tab (ADR-0015 §6). Both old addresses
 * are live links in emails, runbooks and this repo's own history, so they land on the new tab rather than
 * silently on Organisation; `SettingsPage` rewrites the query string so the URL tells the truth afterwards.
 */
export const LEGACY_SETTINGS_TABS: Record<string, SettingsTab> = {
  plan: "subscription",
  billing: "subscription",
};

export const SCHEDULES_TABS = ["templates", "holidays"] as const;
export type SchedulesTab = (typeof SCHEDULES_TABS)[number];

/**
 * M19 (contracts §11): the Staff settings page's tabs, in the order they render. Three lanes fill them —
 * general…sites (C1), the two leave tabs (C4), attendance policies (C5) — through C1's tab registry, and
 * the ids live HERE so the Dashboard's setup-gap cards and every other lane can deep-link a tab without
 * importing another lane's folder.
 */
export const STAFF_SETTINGS_TABS = [
  "general",
  "departments",
  "designations",
  "teams",
  "sites",
  "leave-types",
  "leave-policies",
  "attendance-policies",
] as const;
export type StaffSettingsTab = (typeof STAFF_SETTINGS_TABS)[number];

/**
 * M19 (contracts §11): the approvals inbox's four tabs. `absence` is the api's own word for the pair it
 * files together — permission and on-duty (`/staff/absence-requests`) — and the tab is labelled that way.
 */
export const STAFF_APPROVALS_TABS = ["leave", "absence", "mobile-punches", "overtime"] as const;
export type StaffApprovalsTab = (typeof STAFF_APPROVALS_TABS)[number];

/**
 * M19 lane G (contracts §14.3): the setup guide's fifteen steps, in dependency order — the ids `/staff/setup`
 * opens on (`?step=`). They live HERE, for the reason `STAFF_SETTINGS_TABS` does: the Dashboard panel's gap rows,
 * the register page's way back and the guide's own rail all deep-link a step, and none of them should have to
 * import the setup folder to name one. The page's step catalogue (labels, kinds, what each depends on) is the
 * setup folder's; only the ids are shared.
 */
export const STAFF_SETUP_STEPS = [
  "numbering",
  "terminals",
  "defaults",
  "shifts",
  "default-shift",
  "calendar",
  "attendance-policy",
  "leave",
  "masters",
  "teams",
  "sites",
  "first-employee",
  "enrol",
  "first-punch",
  "finish",
] as const;
export type StaffSetupStep = (typeof STAFF_SETUP_STEPS)[number];

/**
 * M20 (contracts §11): the Student settings page's tabs, in the order they render. C1 fills the first four
 * through its tab registry; C4 registers `sms` (gated `students:sms`). The ids live HERE, for the reason
 * `STAFF_SETTINGS_TABS` does: the setup guide and Today's gap rows deep-link a tab without importing a lane.
 */
export const STUDENT_SETTINGS_TABS = ["general", "terminals", "academic-years", "teachers", "sms"] as const;
export type StudentSettingsTab = (typeof STUDENT_SETTINGS_TABS)[number];

/**
 * M20 C2-09: a student group page's tabs, in the order they render (`members` is the bare address), and the
 * Calendar page's. Here for the reason `STUDENT_SETTINGS_TABS` is: the student page, the day drawer and the
 * setup guide deep-link C2's pages through `ROUTES` without importing the groups folder.
 */
export const STUDENT_GROUP_TABS = ["members", "timetable", "teachers", "sms", "special-days"] as const;
export type StudentGroupTab = (typeof STUDENT_GROUP_TABS)[number];
export const STUDENT_CALENDAR_TABS = ["holidays", "special-days"] as const;
export type StudentCalendarTab = (typeof STUDENT_CALENDAR_TABS)[number];

/**
 * M20 lane G (G-setup-guide.md §2): the Students setup guide's fifteen steps, in dependency order — the ids
 * `/students/setup` opens on (`?step=`). Only the ids are shared; the catalogue (titles, kinds, done-facts) is
 * the setup folder's. A change is a request to lane G.
 */
export const STUDENT_SETUP_STEPS = [
  "plan",
  "terminals",
  "numbering",
  "academic-year",
  "timetable",
  "groups",
  "holidays",
  "students",
  "consents",
  "teachers",
  "sms-balance",
  "sms-policy",
  "first-punch",
  "first-sms",
  "finish",
] as const;
export type StudentSetupStep = (typeof STUDENT_SETUP_STEPS)[number];

/**
 * M25 (contracts §15): the Labour settings page's tabs, in the order they render — Day rules first, so the bare
 * address is that tab. The ids live HERE, for the reason `STUDENT_SETTINGS_TABS` does: the setup guide, Today's
 * gap rows and the labourer page deep-link a tab without importing a lane's folder.
 */
export const LABOUR_SETTINGS_TABS = [
  "rules",
  "numbering",
  "trades",
  "departments",
  "holidays",
  "shifts",
  "lock",
] as const;
export type LabourSettingsTab = (typeof LABOUR_SETTINGS_TABS)[number];

/** M25 (C2-05): a labourer page's four tabs — Profile is the bare address. Here for the reason above. */
export const LABOUR_LABOURER_TABS = ["profile", "attendance", "documents", "movements"] as const;
export type LabourLabourerTab = (typeof LABOUR_LABOURER_TABS)[number];

/**
 * M25 lane G (contracts §15): the Labour setup guide's nine steps, in dependency order — the ids `/labour/setup`
 * opens on (`?step=`). Only the ids are shared; the catalogue (titles, kinds, done-facts) is the setup folder's.
 * A change is a request to lane G.
 */
export const LABOUR_SETUP_STEPS = [
  "rules",
  "trades",
  "contractors",
  "sites",
  "terminals",
  "labourers",
  "photos",
  "first-day",
  "reports",
] as const;
export type LabourSetupStep = (typeof LABOUR_SETUP_STEPS)[number];

/**
 * M26 (contracts §20): the Payroll settings page's tabs, in the order they render — General first, so the bare
 * address is that tab. The ids live HERE for the reason `LABOUR_SETTINGS_TABS` does: the setup guide and the run
 * page deep-link a tab without importing a lane's folder.
 */
export const PAYROLL_SETTINGS_TABS = ["general", "payslip", "components", "templates", "statutory"] as const;
export type PayrollSettingsTab = (typeof PAYROLL_SETTINGS_TABS)[number];

/** M26 (C3-02): an employee's payroll page tabs — Structure is the bare address. Here for the reason above. */
export const PAYROLL_EMPLOYEE_TABS = ["structure", "statutory", "payment", "payslips", "deductions"] as const;
export type PayrollEmployeeTab = (typeof PAYROLL_EMPLOYEE_TABS)[number];

/**
 * M26 (contracts §19, C6): the address segment of each of the module's four reports (`/payroll/reports/:kind`), in
 * the hub's order. The report kinds the export log takes are `PAYROLL_REPORT_KINDS` (`shared/export/sheet.ts`).
 */
export const PAYROLL_REPORT_IDS = ["register", "cost-summary", "payment-status", "headcount-cost"] as const;
export type PayrollReportId = (typeof PAYROLL_REPORT_IDS)[number];

/**
 * M26 lane G (contracts §20): the Payroll setup guide's seven steps, in dependency order — the ids `/payroll/setup`
 * opens on (`?step=`). Only the ids are shared; the catalogue (titles, kinds, done-facts) is the setup folder's.
 * A change is a request to lane G.
 */
export const PAYROLL_SETUP_STEPS = [
  "settings",
  "components",
  "schemes",
  "cycle",
  "people",
  "structures",
  "preview",
] as const;
export type PayrollSetupStep = (typeof PAYROLL_SETUP_STEPS)[number];

/**
 * M21 (contracts §15): the Co-working settings page's tabs, in the order they render — Centre first, so the bare
 * address is that tab (`/coworking/settings/:tab`). The ids live HERE, for the reason `LABOUR_SETTINGS_TABS` does:
 * the setup guide, the dashboard and a refused write ("complete the seller profile") deep-link a tab without
 * importing a lane's folder. A change is a request to lane C1.
 */
export const COWORKING_SETTINGS_TABS = [
  "centre",
  "defaults",
  "seller",
  "series",
  "unpaid-door",
  "capacity",
] as const;
export type CoworkingSettingsTab = (typeof COWORKING_SETTINGS_TABS)[number];

/**
 * M21 lane G (PRD SET-005, route 136): the Co-working setup guide's seven steps, numbered as the api's
 * `setupFactsDto` numbers them (`steps[].id`, 1..7) — the segment `/coworking/setup/:step` opens on. Only the
 * numbers are shared; the catalogue (titles, done-facts) is the setup folder's. A change is a request to lane G.
 */
export const COWORKING_SETUP_STEPS = [1, 2, 3, 4, 5, 6, 7] as const;
export type CoworkingSetupStep = (typeof COWORKING_SETUP_STEPS)[number];

/**
 * Setup guides for Access Control, Gym and Visitors (plans/v2/setup-guides/README.md §2.1): the step ids of each
 * guide, in dependency order — the ids `?step=` carries. They live HERE for the reason `STAFF_SETUP_STEPS` does:
 * a dashboard panel's row, a dialog's way back (`useSetupReturn`) and the guide's own rail all deep-link a step,
 * and none of them should have to import a setup folder to name one. Each guide's catalogue (labels, kinds,
 * purposes, what each step depends on) stays in its module; only the ids are shared. The lists are the ones in
 * the lane files — A §2, B §2, C §2 — and a test in `nav.setup.test.ts` pins them.
 */
export const CORE_SETUP_STEPS = [
  "organisation",
  "terminal",
  "provisioned",
  "first-person",
  "credentials",
  "first-entry",
  "groups",
  "schedules",
  "members",
  "integrations",
  "subscription",
  "finish",
] as const;
export type CoreSetupStep = (typeof CORE_SETUP_STEPS)[number];

export const GYM_SETUP_STEPS = [
  "gym-settings",
  "entry-doors",
  "plans",
  "trainers",
  "first-member",
  "enrol",
  "first-checkin",
  "dues",
  "leads",
  "finish",
] as const;
export type GymSetupStep = (typeof GYM_SETUP_STEPS)[number];

export const VISITORS_SETUP_STEPS = [
  "visitor-doors",
  "qr-terminal",
  "desk",
  "first-pass",
  "first-scan",
  "check-out",
  "hours",
  "finish",
] as const;
export type VisitorsSetupStep = (typeof VISITORS_SETUP_STEPS)[number];

/**
 * The one shape of a setup guide's address, for all four guides: `?step=` ALWAYS — even for step 1, because the
 * bare address opens on the first step that is not done, so `?step=<first>` and no query are different requests —
 * then the record the later steps focus on when there is one: `person` for Access Control, Gym and Staff, `visit`
 * for Visitors. Private on purpose: callers use the four `…SetupStep` builders in `ROUTES`, whose step ids are typed.
 */
function setupAddress(
  base: string,
  step: string,
  focus: { key: "person" | "visit"; id: string | null | undefined },
): string {
  const params = new URLSearchParams({ step });
  if (focus.id) params.set(focus.key, focus.id);
  return `${base}?${params.toString()}`;
}

/**
 * M24 (contracts §11): a Face Kiosk list address — the given filters as `?key=value`, in the order the caller
 * wrote them, empty values dropped, so a bare call is the bare page. Private: callers use the `faceKiosk…`
 * builders in `ROUTES`, whose filter names are typed.
 */
function faceKioskAddress(base: string, q: Record<string, string | boolean | null | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(q)) {
    if (value === null || value === undefined || value === "" || value === false) continue;
    params.set(key, value === true ? "true" : value);
  }
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

/**
 * M25 (contracts §15): a Labour list address — the given filters as `?key=value`, in the order the caller wrote
 * them, empty values dropped, so a bare call is the bare page. Private: callers use the `labour…For` builders in
 * `ROUTES`. Days are `YYYY-MM-DD` organisation days, ids are uuids.
 */
function labourAddress(base: string, q: Record<string, string | null | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(q)) {
    if (value === null || value === undefined || value === "") continue;
    params.set(key, value);
  }
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

/**
 * M26 (contracts §20): a Payroll address with its query — the given values as `?key=value`, in the order the caller
 * wrote them, empty values dropped, so a bare call is the bare page. Private: callers use the `payroll…For` builders
 * in `ROUTES`. Months are `YYYY-MM` payroll labels, financial years `2026-27`, days `YYYY-MM-DD`, ids uuids.
 */
function payrollAddress(base: string, q: Record<string, string | null | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(q)) {
    if (value === null || value === undefined || value === "") continue;
    params.set(key, value);
  }
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

/**
 * M21 (contracts §15): a Co-working list address — the given filters as `?key=value`, in the order the caller
 * wrote them, empty values dropped, so a bare call is the bare page. Private: callers use the `coworking…For`
 * builders in `ROUTES`. Days are `YYYY-MM-DD` organisation days, ids are uuids.
 */
function coworkingAddress(base: string, q: Record<string, string | null | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(q)) {
    if (value === null || value === undefined || value === "") continue;
    params.set(key, value);
  }
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

export const ROUTES = {
  help: "/help",
  helpModule: (slug: string) => `/help/${slug}`,
  login: "/login",
  /** Public, no session: the draft consent documents the sign-up acceptance links to (M6 §7). */
  legalTerms: "/legal/terms",
  legalPrivacy: "/legal/privacy",
  accountDeletion: "/account-deletion",
  /** Public, no session: every release of the web portal and the mobile app, newest first (`shared/releases`). */
  whatsNew: "/whats-new",
  /**
   * Public, no session: the visitor pass link. The code is the whole query string, `/v?K7Q2M9XWPA2B`, because
   * DLT whitelists a dynamic SMS link by a fixed part that must end in "?" (plan visitor-pass-link decision 12).
   */
  passLink: "/v",
  onboardingCreateOrganisation: "/onboarding/create-organisation",
  onboardingInvitations: "/onboarding/invitations",
  overview: "/overview",
  events: "/events",
  /**
   * The event log, already narrowed — the way out of a report into the punches behind a number (M12 §4).
   * These parameter names are the log's own URL contract (`modules/events/utils/filters.ts`); a test in the
   * reports module round-trips a link through that parser, so the two cannot drift apart silently.
   */
  eventsFor: (q: {
    from: string;
    to: string;
    deviceId?: string | null;
    personId?: string | null;
    result?: "granted" | "denied";
  }): string => {
    const params = new URLSearchParams({ from: q.from, to: q.to });
    if (q.deviceId) params.set("device", q.deviceId);
    if (q.personId) params.set("person", q.personId);
    if (q.result) params.set("result", q.result);
    return `/events?${params.toString()}`;
  },
  devices: "/devices",
  device: (id: string) => `/devices/${id}`,
  people: "/people",
  person: (id: string) => `/people/${id}`,
  /**
   * M13 §3: access groups live under People — a group is a way to edit people's access, not a
   * section of its own, so there is no nav entry and the People header carries the switch.
   */
  groups: "/people/groups",
  group: (id: string) => `/people/groups/${id}`,
  doors: "/doors",
  /**
   * M12 §3: `/reports` is the hub the nav entry points at, and each report has its own address under it.
   * `/reports/attendance` keeps the M9 address it has always had — it is in links, runbooks and emails.
   * The core hub carries only the core reports (ADR-0017 addendum 2026-09-15) — the five ledger reports
   * moved to the Gym module's own hub, below.
   */
  reports: "/reports",
  reportsAttendance: "/reports/attendance",
  reportsSummary: "/reports/summary",
  reportsMuster: "/reports/muster",
  reportsTerminals: "/reports/terminals",
  reportsDenied: "/reports/denied",
  reportsEnrolment: "/reports/enrolment",
  /**
   * The Gym module's own workspace (ADR-0017 addendum 2026-09-15 and its **second** addendum of the
   * same date): a module is a workspace of its own, reached from the sidebar's workspace switcher and
   * revealed by its add-on, never folded into the core the way `/ledger` and `/reports/ledger/*` were.
   *
   * `gymMember(personId)` is the correction that matters most: a person's membership, plan and ledger
   * account live HERE, keyed on the shared `people.id` (ADR-0017 decision 1 — a module never creates a
   * parallel identity), never as cards on the core person page, which cannot express someone who is a
   * gym client *and* a gym trainer. The page is keyed on the person, not on one relationship, so two
   * relationships are two sections of one page rather than two pages fighting over one person.
   *
   * `gymLedger` is the dues list (M14 §4, ADR-0019) — the organisation charging its OWN people, never
   * `settingsTab("subscription")`, which is Nexborg charging the organisation. `gymReports` is the
   * module's own reports hub, carrying the five ledger reports of M14 §4 wave 3b (ADR-0019 decision 9).
   */
  gym: "/gym",
  /**
   * M17: the gym's enquiries. Between the dashboard and the members because that is the order the
   * business runs in — somebody asks, and then somebody joins — and `gymLead(leadId)` is the drawer
   * on the same page rather than a page of its own, so opening one never loses the call list behind
   * it while the address still survives a refresh and the back button.
   *
   * Keyed on the LEAD and never on a person, which is the opposite of `gymMember(personId)` and for
   * the reason `visitor(visitorId)` is: a prospect has no `people` row at all (ADR-0017's prospects
   * addendum, 2026-09-22), and most of them never will.
   */
  gymLeads: "/gym/leads",
  gymLead: (leadId: string) => `/gym/leads/${leadId}`,
  gymMembers: "/gym/members",
  gymMember: (personId: string) => `/gym/members/${personId}`,
  gymPlans: "/gym/plans",
  /** TRN-001..003: the trainer roster — add, remove, and (from a member's own page) assign. */
  gymTrainers: "/gym/trainers",
  gymLedger: "/gym/ledger",
  gymReports: "/gym/reports",
  /** M15 §1 row 10.2: the module's own two reports, on its own hub — never the core Reports hub. */
  gymReportsMemberships: "/gym/reports/memberships",
  gymReportsDues: "/gym/reports/dues",
  /** M17 plan §6.4: leads and conversions, in the MODULE's hub — never the core `/reports` one. */
  gymReportsLeads: "/gym/reports/leads",
  /**
   * M22 (plan `plans/v2/M22-gym-attendance.md` §6): the gym's four attendance reports, first on its hub.
   * Member attendance holds its filters in the URL (D14), so `gymAttendanceFor` is how the dashboard's
   * "See who" and the member page's "Visits" deep-link into it — never a hand-built query string.
   */
  gymReportsVisits: "/gym/reports/visits",
  gymReportsAttendance: "/gym/reports/attendance",
  gymReportsInactive: "/gym/reports/inactive",
  gymReportsPeakHours: "/gym/reports/peak-hours",
  gymAttendanceFor: (opts: { personId?: string; from?: string; to?: string } = {}): string => {
    const params = new URLSearchParams();
    if (opts.personId) params.set("personId", opts.personId);
    if (opts.from) params.set("from", opts.from);
    if (opts.to) params.set("to", opts.to);
    const query = params.toString();
    return query ? `/gym/reports/attendance?${query}` : "/gym/reports/attendance";
  },
  gymReportsOutstanding: "/gym/reports/outstanding",
  gymReportsPayments: "/gym/reports/payments",
  gymReportsStatement: "/gym/reports/statement",
  gymReportsReceipts: "/gym/reports/receipts",
  gymReportsCorrections: "/gym/reports/corrections",
  /** GYM-002, GYM-003: grace, the dues flag (never a door), the entry group. */
  gymSettings: "/gym/settings",
  /**
   * The Gym setup guide (setup-guides plan, lane B): ten steps from an empty gym to the first check-in. Reached from the
   * Dashboard's header and its "Set the gym up" panel, the first line of Gym settings, **its own sidebar entry** ("Setup
   * guide", last in the Gym list, after Gym settings) and by bookmark. `gymSetupStep` always writes `?step=`; `person`
   * focuses the steps about one member (the first member, enrolling them, their first check-in, their dues) — the
   * register dialog returns there with the member it just made.
   */
  gymSetup: "/gym/setup",
  gymSetupStep: (step: GymSetupStep, opts?: { person?: string | null }): string =>
    setupAddress("/gym/setup", step, { key: "person", id: opts?.person }),
  /**
   * M16 wave 2 (ADR-0021 decision 9): the Visitors module's own workspace — never a menu on the core
   * sidebar and never a page on the Reports hub (the module-navigation rule).
   *
   * `visitor(visitorId)` is keyed on the DURABLE visitor and not on a person, which is the opposite
   * of `gymMember(personId)` and for a reason: a visitor's `people` row is transient — minted on
   * issue and soft-deleted when the pass expires (decision 9) — so a person-keyed URL would be a
   * bookmark that stops resolving the day after the visit. The visitor row is the thing that lasts.
   */
  visitors: "/visitors",
  visitor: (visitorId: string) => `/visitors/${visitorId}`,
  /** The register: every visit, newest first, with "in the building now" one click away. */
  visits: "/visitors/visits",
  /**
   * M16 wave 4 (ADR-0021 open question 4): QR passes being scanned at the doors, live. The terminal
   * beeps identically for an open and a refusal, so without this page a refused visitor cannot be
   * helped — the refusal reason exists in the decision and in no row to look up afterwards.
   */
  visitorScans: "/visitors/scans",
  /**
   * Visitors → Settings (visitor pass link, 2026-10-18): the pass-link switches and what one text costs. The module's own
   * page, never a Settings tab (the module-navigation rule). Declared BEFORE `visitors/:visitorId` in the route table.
   */
  visitorsSettings: "/visitors/settings",
  /**
   * The Visitors setup guide (setup-guides plan, lane C): eight steps from an empty register to a visitor through the door
   * and out again. Reached from the register's header and its empty state, the Live scans page's note, **its own sidebar
   * entry** ("Setup guide", last in the Visitors list, after Live scans) and by bookmark. `visitorsSetupStep` always
   * writes `?step=`; `visit` (not `person`: a visitor's person row is transient, the visit is what lasts) focuses the
   * steps about one pass — issuing it, its first scan, checking out. The route is declared BEFORE `visitors/:visitorId`,
   * or the guide would open as a visitor whose id is the word "setup".
   */
  visitorsSetup: "/visitors/setup",
  visitorsSetupStep: (step: VisitorsSetupStep, opts?: { visit?: string | null }): string =>
    setupAddress("/visitors/setup", step, { key: "visit", id: opts?.visit }),
  /**
   * M19 (contracts §11): the Staff workspace — its own prefix, revealed by the `staff` add-on, never an
   * entry on the core sidebar or a card on the core Reports hub (ADR-0017's module-navigation rule).
   *
   * `staffEmployee(personId)` is keyed on the shared `people.id`, like `gymMember(personId)` and for the
   * same reason: an employee IS a person (ADR-0017 decision 1), so the core person page's "Also in:
   * Staff" link and the module's own page name the same human by the same id, and a rejoin is the same
   * page rather than a second one. `new` and `import` are static segments beside it.
   */
  staff: "/staff",
  staffEmployees: "/staff/employees",
  staffEmployee: (personId: string) => `/staff/employees/${personId}`,
  staffEmployeeNew: "/staff/employees/new",
  staffEmployeesImport: "/staff/employees/import",
  /**
   * The month grid, and the same page with one day's drawer open: `?person&day` is the drawer's address
   * (contracts §11), so a bookmark, a refresh and the back button all land on it. `month` narrows the
   * grid — the employee page's "attendance this month" link (C3-11). Every parameter is optional.
   */
  staffAttendance: "/staff/attendance",
  staffAttendanceFor: (q: { person?: string | null; day?: string | null; month?: string | null }): string => {
    const params = new URLSearchParams();
    if (q.month) params.set("month", q.month);
    if (q.person) params.set("person", q.person);
    if (q.day) params.set("day", q.day);
    const query = params.toString();
    return query ? `/staff/attendance?${query}` : "/staff/attendance";
  },
  /**
   * The Monthly MIS (D wave, contracts §13.5) and its five drill-downs — `presence | leave | late |
   * overtime | unresolved`. Their own scope (`?month&team&department&q`) travels as a query string.
   */
  staffMis: "/staff/mis",
  staffMisMetric: (metric: string) => `/staff/mis/${metric}`,
  /** Leave, permission & on-duty, mobile punches and overtime — one inbox, four tabs (`?tab=`). */
  staffApprovals: "/staff/approvals",
  staffApprovalsTab: (tab: StaffApprovalsTab) =>
    tab === "leave" ? "/staff/approvals" : `/staff/approvals?tab=${tab}`,
  staffRequests: "/staff/requests",
  staffMobilePunches: "/staff/mobile-punches",
  /**
   * The Roster nav entry's address. It is not a page: it lands on Shifts, the way `/reports` lands on
   * Attendance — and it is the entry's `to` so that all five roster pages light the one Roster entry.
   */
  staffRoster: "/staff/roster",
  staffRosterShifts: "/staff/roster/shifts",
  staffRosterAssignments: "/staff/roster/assignments",
  staffRosterHolidays: "/staff/roster/holidays",
  staffRosterOverrides: "/staff/roster/overrides",
  staffRosterCompOffs: "/staff/roster/comp-offs",
  staffPeriods: "/staff/periods",
  /** One month, as `YYYY-MM` — the period's own key (`/staff/periods/:month/…` on the api). */
  staffPeriod: (month: string) => `/staff/periods/${month}`,
  /** The module's own reports hub (C5) — never the core `/reports` one. */
  staffReports: "/staff/reports",
  staffReport: (kind: string) => `/staff/reports/${kind}`,
  staffSettings: "/staff/settings",
  staffSettingsTab: (tab: StaffSettingsTab) =>
    tab === "general" ? "/staff/settings" : `/staff/settings?tab=${tab}`,
  /**
   * The setup guide (lane G). Reached from the Dashboard's header, the "Before the numbers are right" panel,
   * the Staff settings → General line, **its own sidebar entry** ("Setup guide", last in the Staff list — Kumar
   * changed his first answer of "no sidebar entry" on 2026-10-01 22:55, so ⌘K lists it too) and by bookmark.
   * The bare address opens on the first step that is not done, which is why `staffSetupStep` always writes
   * `?step=` — even for step 1, whose "default" is not a fixed step on a return visit.
   */
  staffSetup: "/staff/setup",
  /**
   * One step of the guide; `person` focuses steps 12–14 (first employee, enrol, first punch) on one
   * employee — the register page returns there with the person it just made.
   */
  staffSetupStep: (step: StaffSetupStep, opts?: { person?: string | null }): string =>
    setupAddress("/staff/setup", step, { key: "person", id: opts?.person }),
  /**
   * M20 (contracts §11): the Student Attendance workspace — its own prefix, revealed by the
   * `student_attendance` add-on (`features.studentAttendance`), never an entry on the core sidebar.
   * `studentsStudent(personId)` is keyed on the shared `people.id`, like `staffEmployee`: a student IS a
   * person (ADR-0025), so the core person page's "Also in: Students" link and the module's own page name
   * the same human by the same id. The section is `/students/students` because `/students` is Today.
   */
  students: "/students",
  studentsStudents: "/students/students",
  studentsStudentNew: "/students/students/new",
  studentsStudentsImport: "/students/students/import",
  studentsStudent: (personId: string) => `/students/students/${personId}`,
  /** The register grid; `person` + `day` open the day drawer, `month` picks the grid's month (`YYYY-MM`). */
  studentsAttendance: "/students/attendance",
  studentsAttendanceFor: (q: {
    person?: string | null;
    day?: string | null;
    month?: string | null;
  }): string => {
    const params = new URLSearchParams();
    if (q.month) params.set("month", q.month);
    if (q.person) params.set("person", q.person);
    if (q.day) params.set("day", q.day);
    const query = params.toString();
    return query ? `/students/attendance?${query}` : "/students/attendance";
  },
  studentsGroups: "/students/groups",
  studentsGroup: (groupId: string) => `/students/groups/${groupId}`,
  /** `/students/groups/:id`, `?tab=` for any tab but Members, `&on=` for the members' day (C2-09). */
  studentsGroupTab: (
    groupId: string,
    tab: StudentGroupTab = "members",
    opts: { on?: string | null } = {},
  ): string => {
    const params = new URLSearchParams();
    if (tab !== "members") params.set("tab", tab);
    if (opts.on) params.set("on", opts.on);
    const query = params.toString();
    return query ? `/students/groups/${groupId}?${query}` : `/students/groups/${groupId}`;
  },
  studentsTimetables: "/students/timetables",
  studentsCalendar: "/students/calendar",
  /** `/students/calendar?month&group&tab` — every part optional; Holidays is the bare address. */
  studentsCalendarFor: (
    q: { month?: string | null; group?: string | null; tab?: StudentCalendarTab | null } = {},
  ): string => {
    const params = new URLSearchParams();
    if (q.tab && q.tab !== "holidays") params.set("tab", q.tab);
    if (q.month) params.set("month", q.month);
    if (q.group) params.set("group", q.group);
    const query = params.toString();
    return query ? `/students/calendar?${query}` : "/students/calendar";
  },
  studentsPromotions: "/students/promotions",
  /**
   * `/students/promotions?group&person…` — `group` is the source group; one `person` is a single transfer
   * (the student page's "Transfer"), several are the members ticked on a group's Members tab.
   */
  studentsPromotionsFor: (
    q: { group?: string | null; person?: string | readonly string[] | null } = {},
  ): string => {
    const params = new URLSearchParams();
    if (q.group) params.set("group", q.group);
    const people = typeof q.person === "string" ? [q.person] : (q.person ?? []);
    for (const p of people) params.append("person", p);
    const query = params.toString();
    return query ? `/students/promotions?${query}` : "/students/promotions";
  },
  /**
   * Not nav entries (the module's menu stays short): C2's exceptions list (`?person=`) and C4's log of the
   * SMS sent to guardians (`?group&person&day&status`), reached from the student page, the day drawer and
   * the Reports hub. W0-C added both so neither lane needs a route request.
   */
  studentsExceptions: "/students/exceptions",
  /** `/students/exceptions?person&group` — one student's exceptions, a group's, or every one. */
  studentsExceptionsFor: (q: { person?: string | null; group?: string | null } = {}): string => {
    const params = new URLSearchParams();
    if (q.person) params.set("person", q.person);
    if (q.group) params.set("group", q.group);
    const query = params.toString();
    return query ? `/students/exceptions?${query}` : "/students/exceptions";
  },
  studentsSms: (
    q: { group?: string | null; person?: string | null; day?: string | null; status?: string | null } = {},
  ): string => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(q)) if (value) params.set(key, value);
    const query = params.toString();
    return query ? `/students/sms?${query}` : "/students/sms";
  },
  studentsReports: "/students/reports",
  studentsReport: (kind: string) => `/students/reports/${kind}`,
  studentsSettings: "/students/settings",
  studentsSettingsTab: (tab: StudentSettingsTab) =>
    tab === "general" ? "/students/settings" : `/students/settings?tab=${tab}`,
  /**
   * The setup guide (lane G): last in the Students list. The bare address opens on the first step that is
   * not done, which is why `studentsSetupStep` always writes `?step=`; `person` focuses the steps about one
   * student (first punch, first SMS).
   */
  studentsSetup: "/students/setup",
  studentsSetupStep: (step: StudentSetupStep, opts?: { person?: string | null }): string =>
    setupAddress("/students/setup", step, { key: "person", id: opts?.person }),
  /**
   * M24 (contracts §11, ADR-0026): the Face Kiosk workspace — its own section, never the core sidebar or the
   * Reports hub (the module-navigation rule). Seats is the bare address; a seat's detail is a drawer on it
   * (`?seat=`), not a page. The filters are the C1 pages' URL contract; a day is `YYYY-MM-DD` (org day).
   */
  faceKiosk: "/face-kiosk",
  faceKioskSeat: (seatId: string) => faceKioskAddress("/face-kiosk", { seat: seatId }),
  faceKioskScans: (
    q: {
      seat?: string | null;
      status?: string | null;
      from?: string | null;
      to?: string | null;
      person?: string | null;
      selfScan?: boolean | null;
    } = {},
  ): string => faceKioskAddress("/face-kiosk/scans", q),
  faceKioskApprovals: (
    q: { seat?: string | null; kind?: string | null; from?: string | null; to?: string | null } = {},
  ): string => faceKioskAddress("/face-kiosk/approvals", q),
  /** `?group=` drills into one list (an access group, a staff team, a student group); bare = every list. */
  faceKioskConsent: (q: { group?: string | null } = {}): string => faceKioskAddress("/face-kiosk/consent", q),
  faceKioskSettings: "/face-kiosk/settings",
  /**
   * M25 (contracts §15, ADR-0027): the Labour workspace — its own prefix, revealed by the `labour_attendance`
   * add-on (`features.labourAttendance`), never an entry on the core sidebar or a card on the core Reports hub.
   * `labourLabourer(personId)` is keyed on the shared `people.id`: a labourer IS a person (ADR-0017), so the core
   * person page's "Also in: Labour" link and the module's own page name the same human by the same id. The
   * section is `/labour/labourers` because `/labour` is Today.
   */
  labour: "/labour",
  labourLabourers: "/labour/labourers",
  /** `/labour/labourers?site&contractor&trade&department&skill&status&q…` — the list's filters, as the caller names them. */
  labourLabourersFor: (q: Record<string, string | null | undefined> = {}): string =>
    labourAddress("/labour/labourers", q),
  labourRegister: "/labour/labourers/register",
  /** `?rejoin=<personId>` opens the register form on a left labourer who is coming back. */
  labourRegisterFor: (q: { rejoin?: string | null } = {}): string =>
    labourAddress("/labour/labourers/register", q),
  labourImport: "/labour/labourers/import",
  /** The phone-like photo round, for one site when it is given (`?site=`). */
  labourPhotoRound: "/labour/labourers/photo-round",
  labourPhotoRoundFor: (q: { site?: string | null } = {}): string =>
    labourAddress("/labour/labourers/photo-round", q),
  labourLabourer: (personId: string) => `/labour/labourers/${personId}`,
  /** A labourer's page on one tab: Profile is the bare address, any other behind `?tab=`. */
  labourLabourerTab: (personId: string, tab: LabourLabourerTab): string =>
    tab === "profile" ? `/labour/labourers/${personId}` : `/labour/labourers/${personId}?tab=${tab}`,
  labourContractors: "/labour/contractors",
  labourContractor: (contractorId: string) => `/labour/contractors/${contractorId}`,
  labourSites: "/labour/sites",
  labourSite: (siteId: string) => `/labour/sites/${siteId}`,
  labourAttendance: "/labour/attendance",
  /** `/labour/attendance?site&contractor&from&to&person&day` — every part optional; `day` opens the day drawer. */
  labourAttendanceFor: (
    q: {
      site?: string | null;
      contractor?: string | null;
      from?: string | null;
      to?: string | null;
      person?: string | null;
      day?: string | null;
      /** M25 shifts: a shift id, or `none` for the days with no shift. */
      shift?: string | null;
    } = {},
  ): string => labourAddress("/labour/attendance", q),
  labourExceptions: "/labour/attendance/exceptions",
  /** `/labour/attendance/exceptions?site&contractor&from&to&kind` — every part optional. */
  labourExceptionsFor: (
    q: {
      site?: string | null;
      contractor?: string | null;
      from?: string | null;
      to?: string | null;
      kind?: string | null;
    } = {},
  ): string => labourAddress("/labour/attendance/exceptions", q),
  /** The module's own reports hub — never a card on the core `/reports` one (ADR-0017 addendum). */
  labourReports: "/labour/reports",
  labourReport: (kind: string) => `/labour/reports/${kind}`,
  /** A report already narrowed (`?site&contractor&from&to&asOf&…`), as the report's own page names its filters. */
  labourReportFor: (kind: string, q: Record<string, string | null | undefined> = {}): string =>
    labourAddress(`/labour/reports/${kind}`, q),
  /** M25 shifts (R16): the Shift-wise attendance report. */
  labourReportShiftWise: "/labour/reports/shift-wise",
  labourSettings: "/labour/settings",
  labourSettingsTab: (tab: LabourSettingsTab) =>
    tab === "rules" ? "/labour/settings" : `/labour/settings?tab=${tab}`,
  /**
   * The setup guide (lane G): last in the Labour list. The bare address opens on the first step that is not done,
   * which is why `labourSetupStep` always writes `?step=`; `person` focuses the steps about one labourer (the
   * photo and the first day).
   */
  labourSetup: "/labour/setup",
  labourSetupStep: (step: LabourSetupStep, opts?: { person?: string | null }): string =>
    setupAddress("/labour/setup", step, { key: "person", id: opts?.person }),
  /**
   * M26 (contracts §20, ADR-0031): the Payroll workspace — its own prefix, revealed by the `payroll` add-on
   * (`features.payroll`, which is false while Staff is not live), never an entry on the core sidebar or a card on the
   * core Reports hub. `/payroll` is the runs list. A month is the `YYYY-MM` label of a Staff period, never a calendar
   * range. `payrollEmployee(personId)` is keyed on the shared `people.id`: an employee IS a person (ADR-0017), so the
   * core person page's "Also in: Payroll" link lands there.
   */
  payroll: "/payroll",
  payrollRun: (month: string) => `/payroll/runs/${month}`,
  /** A run's page with its payslip grid narrowed: `?status&q`, as the grid names its filters. */
  payrollRunFor: (month: string, q: { status?: string | null; q?: string | null } = {}): string =>
    payrollAddress(`/payroll/runs/${month}`, q),
  payrollPayslip: (month: string, personId: string) => `/payroll/runs/${month}/payslips/${personId}`,
  payrollEmployees: "/payroll/employees",
  /** `/payroll/employees?asOf&q&department&designation&team&without…` — the list's filters, as the caller names them. */
  payrollEmployeesFor: (q: Record<string, string | null | undefined> = {}): string =>
    payrollAddress("/payroll/employees", q),
  payrollEmployeesImport: "/payroll/employees/import",
  payrollEmployeesBulk: "/payroll/employees/bulk",
  payrollEmployee: (personId: string) => `/payroll/employees/${personId}`,
  /** An employee's payroll page on one tab: Structure is the bare address, any other behind `?tab=`. */
  payrollEmployeeTab: (personId: string, tab: PayrollEmployeeTab): string =>
    tab === "structure" ? `/payroll/employees/${personId}` : `/payroll/employees/${personId}?tab=${tab}`,
  payrollInputs: "/payroll/inputs",
  /** `/payroll/inputs?month&person&kind` — every part optional. */
  payrollInputsFor: (
    q: { month?: string | null; person?: string | null; kind?: string | null } = {},
  ): string => payrollAddress("/payroll/inputs", q),
  payrollTax: "/payroll/tax",
  /** `/payroll/tax?fy&status` — the declarations list for a financial year (`2026-27`). */
  payrollTaxFor: (q: { fy?: string | null; status?: string | null } = {}): string =>
    payrollAddress("/payroll/tax", q),
  payrollDeclaration: (id: string) => `/payroll/tax/declarations/${id}`,
  payrollTaxSettings: "/payroll/tax/settings",
  payrollTaxSettingsFor: (q: { fy?: string | null } = {}): string =>
    payrollAddress("/payroll/tax/settings", q),
  payrollPriorIncome: "/payroll/tax/prior-income",
  payrollPriorIncomeFor: (q: { fy?: string | null; person?: string | null } = {}): string =>
    payrollAddress("/payroll/tax/prior-income", q),
  payrollPayments: "/payroll/payments",
  payrollPaymentsMonth: (month: string) => `/payroll/payments/${month}`,
  /** The module's own reports hub — never a card on the core `/reports` one (ADR-0017 addendum). */
  payrollReports: "/payroll/reports",
  payrollReport: (kind: PayrollReportId) => `/payroll/reports/${kind}`,
  /** A report already narrowed (`?month&from&to&groupBy…`), as the report's own page names its filters. */
  payrollReportFor: (kind: PayrollReportId, q: Record<string, string | null | undefined> = {}): string =>
    payrollAddress(`/payroll/reports/${kind}`, q),
  payrollSettings: "/payroll/settings",
  payrollSettingsTab: (tab: PayrollSettingsTab) =>
    tab === "general" ? "/payroll/settings" : `/payroll/settings?tab=${tab}`,
  /**
   * The setup guide (lane G): last in the Payroll list. The bare address opens on the first step that is not done,
   * which is why `payrollSetupStep` always writes `?step=`; `person` focuses the steps about one employee.
   */
  payrollSetup: "/payroll/setup",
  payrollSetupStep: (step: PayrollSetupStep, opts?: { person?: string | null }): string =>
    setupAddress("/payroll/setup", step, { key: "person", id: opts?.person }),
  /**
   * M21 (contracts §15, ADR-0028, ADR-0029): the Co-working workspace — its own prefix, revealed by the
   * `coworking` add-on (`features.coworking`), never an entry on the core sidebar or a card on the core Reports
   * hub. `coworkingMember(personId)` is keyed on the shared `people.id`: a member IS a person (ADR-0017), so the
   * core person page's "Also in: Co-working" link and the module's own page name the same human by the same id.
   * A company is a ledger party, keyed on its own id. "Invoices" is the operator's own tax document — never
   * Nexborg's subscription invoices (ADR-0019 addendum, item 3). `/coworking` is the dashboard; static
   * segments (`new`) are written before the parameter beside them in the route table.
   */
  coworking: "/coworking",
  coworkingCompanies: "/coworking/companies",
  /** `/coworking/companies?q&archived` — every part optional. */
  coworkingCompaniesFor: (q: { q?: string | null; archived?: string | null } = {}): string =>
    coworkingAddress("/coworking/companies", q),
  coworkingCompany: (companyId: string) => `/coworking/companies/${companyId}`,
  /** The company form: the list page in its "new company" mode (M21 C2 R4; there is no route of its own). */
  coworkingCompanyNew: "/coworking/companies?new=1",
  /** The company page in its "edit details" mode (M21 C2 R4). */
  coworkingCompanyEdit: (companyId: string) => `/coworking/companies/${companyId}?edit=1`,
  coworkingMembers: "/coworking/members",
  /** `/coworking/members?q&companyId&state` — every part optional. */
  coworkingMembersFor: (
    q: { q?: string | null; companyId?: string | null; state?: string | null } = {},
  ): string => coworkingAddress("/coworking/members", q),
  coworkingMemberNew: "/coworking/members/new",
  /** Register a member: `?company=<id>` pre-selects the company, `?adopt=<personId>` starts from a person. */
  coworkingMemberNewFor: (q: { company?: string | null; adopt?: string | null } = {}): string =>
    coworkingAddress("/coworking/members/new", q),
  coworkingMember: (personId: string) => `/coworking/members/${personId}`,
  coworkingContracts: "/coworking/contracts",
  /** `/coworking/contracts?state&holder&expiringWithin&q` — every part optional. */
  coworkingContractsFor: (
    q: {
      state?: string | null;
      holder?: string | null;
      expiringWithin?: string | null;
      q?: string | null;
    } = {},
  ): string => coworkingAddress("/coworking/contracts", q),
  coworkingContractNew: "/coworking/contracts/new",
  /** Start a draft for one holder: `?holderKind=company|member&holderId=<id>`. */
  coworkingContractNewFor: (q: { holderKind?: string | null; holderId?: string | null } = {}): string =>
    coworkingAddress("/coworking/contracts/new", q),
  coworkingContract: (contractId: string) => `/coworking/contracts/${contractId}`,
  coworkingInvoices: "/coworking/invoices",
  /** `/coworking/invoices?status&holderKind&holderId&from&to&q` — every part optional. */
  coworkingInvoicesFor: (q: Record<string, string | null | undefined> = {}): string =>
    coworkingAddress("/coworking/invoices", q),
  /** Any tax document of the module — an invoice, a note, a demand — opens on its invoice address. */
  coworkingDocument: (documentId: string) => `/coworking/invoices/${documentId}`,
  coworkingCreditNotes: "/coworking/credit-notes",
  coworkingPayments: "/coworking/payments",
  /** `/coworking/payments?holderKind&holderId&from&to&method` — every part optional. */
  coworkingPaymentsFor: (q: Record<string, string | null | undefined> = {}): string =>
    coworkingAddress("/coworking/payments", q),
  coworkingDeposits: "/coworking/deposits",
  /**
   * `/coworking/deposits?contractId&holder` — every part optional. `holder` is `party:<uuid>` or `person:<uuid>`,
   * the ledger's own kind (a company is a party, an individual member a person); the page never guesses which.
   */
  coworkingDepositsFor: (q: { contractId?: string | null; holder?: string | null } = {}): string =>
    coworkingAddress("/coworking/deposits", q),
  coworkingTds: "/coworking/tds",
  /**
   * `/coworking/tds?holder&fy&quarter&pending` — every part optional; `fy` is `2026-27`. `holder` is
   * `party:<uuid>` or `person:<uuid>`, the ledger's own kind (a company is a party, a member a person).
   */
  coworkingTdsFor: (
    q: {
      holder?: string | null;
      fy?: string | null;
      quarter?: string | null;
      pending?: string | null;
    } = {},
  ): string => coworkingAddress("/coworking/tds", q),
  coworkingStatements: "/coworking/statements",
  /** The accounts list already narrowed: `?overdueOnly=true` is the dashboard's Overdue figure (C6 R4). */
  coworkingStatementsFor: (q: { overdueOnly?: string | null } = {}): string =>
    coworkingAddress("/coworking/statements", q),
  /** One holder's statement: a person's or a party's (a company's, a walk-in's), by the ledger's own kind. */
  coworkingStatement: (kind: "person" | "party", id: string) => `/coworking/statements/${kind}/${id}`,
  coworkingStatementFor: (
    kind: "person" | "party",
    id: string,
    q: { from?: string | null; to?: string | null } = {},
  ): string => coworkingAddress(`/coworking/statements/${kind}/${id}`, q),
  /** The billing runs list; a run's page and its documents beneath it. */
  coworkingBilling: "/coworking/billing",
  coworkingBillingRun: (runId: string) => `/coworking/billing/${runId}`,
  /** Ready to block, and the blocked accounts beside it. */
  coworkingUnpaid: "/coworking/unpaid",
  /** The unpaid page on its Blocked tab (`?tab=blocked`); bare = Ready to block (C6 R4). */
  coworkingUnpaidFor: (q: { tab?: "blocked" | null } = {}): string =>
    coworkingAddress("/coworking/unpaid", q),
  coworkingDayPasses: "/coworking/day-passes",
  /** `/coworking/day-passes?day=YYYY-MM-DD` — bare = today in the organisation's day. */
  coworkingDayPassesFor: (q: { day?: string | null } = {}): string =>
    coworkingAddress("/coworking/day-passes", q),
  coworkingProducts: "/coworking/products",
  /** The module's own reports hub — never a card on the core `/reports` one (ADR-0017 addendum). */
  coworkingReports: "/coworking/reports",
  coworkingReport: (reportId: string) => `/coworking/reports/${reportId}`,
  /** A report already narrowed (`?month&from&to&fy&quarter&…`), as the report's own page names its filters. */
  coworkingReportFor: (reportId: string, q: Record<string, string | null | undefined> = {}): string =>
    coworkingAddress(`/coworking/reports/${reportId}`, q),
  coworkingImport: "/coworking/import",
  /** `?kind=members|contracts|opening_documents` opens the import on one of the three cut-over files. */
  coworkingImportFor: (q: { kind?: string | null } = {}): string => coworkingAddress("/coworking/import", q),
  coworkingSettings: "/coworking/settings",
  coworkingSettingsTab: (tab: CoworkingSettingsTab) =>
    tab === "centre" ? "/coworking/settings" : `/coworking/settings/${tab}`,
  /** The setup guide (lane G): last in the Co-working list. The bare address opens on the first open step. */
  coworkingSetup: "/coworking/setup",
  coworkingSetupStep: (step: CoworkingSetupStep) => `/coworking/setup/${step}`,
  /**
   * M27 (contracts §18, ADR-0030 addendum 2026-10-28): the Rooms and Equipment workspace — its own prefix, revealed by
   * the `rooms` add-on (`features.rooms`), never an entry on the core sidebar or a card on the core Reports hub.
   * `/rooms` itself redirects to Overview. Bookings opens on the calendar; `?view=list` is the list. The step
   * builders of the setup guide arrive with lane G (a Request on this file), as the other modules' did.
   */
  rooms: "/rooms",
  roomsOverview: "/rooms/overview",
  roomsBookings: "/rooms/bookings",
  roomsBookingsList: "/rooms/bookings?view=list",
  roomsBooking: (bookingId: string) => `/rooms/bookings/${bookingId}`,
  roomsResources: "/rooms/resources",
  roomsResourceNew: "/rooms/resources/new",
  roomsResource: (resourceId: string) => `/rooms/resources/${resourceId}`,
  roomsReports: "/rooms/reports",
  roomsReport: (kind: string) => `/rooms/reports/${kind}`,
  roomsDisplays: "/rooms/displays",
  roomsSettings: "/rooms/settings",
  roomsSetup: "/rooms/setup",
  /**
   * Public, no session, no layout (contracts §18; lane C4 builds the page): the wall display of one room's busy and
   * free schedule. It authenticates with its own paired token, never the portal session, so it is an exact public
   * path (`PUBLIC_EXACT_PATHS`) — `/displays` and `/display/x` are not.
   */
  display: "/display",
  /** M9 §5: templates and holiday groups, two tabs on one page (`?tab=`). */
  schedules: "/schedules",
  schedulesTab: (tab: SchedulesTab) => (tab === "templates" ? "/schedules" : `/schedules?tab=${tab}`),
  /**
   * The Members & roles surfaces now live in Settings. This address predates that and still resolves
   * — it is in invitation emails, runbooks and this repo's own history — so it redirects rather than
   * 404s, like `/ledger` and `/reports/ledger/*` before it.
   */
  members: "/settings?tab=members",
  roles: "/settings?tab=roles",
  settings: "/settings",
  /** Dedicated, bookmarkable plan editor. The overview remains at Settings → Subscription. */
  subscriptionChange: "/settings/subscription/change",
  /** Settings tabs live in the URL (`?tab=`), like the members page. */
  settingsTab: (tab: SettingsTab) => (tab === "organisation" ? "/settings" : `/settings?tab=${tab}`),
  /**
   * Subscription with the plan configurator open and one item ticked — the comparison's own "Add", reached
   * from Settings → Modules (`useSubscriptionDeepLink`). Spent once: the page drops it from the URL.
   */
  subscriptionAdd: (code: string) => `/settings/subscription/change?addItem=${encodeURIComponent(code)}`,
  /**
   * The Access Control setup guide (setup-guides plan, lane A): twelve steps from an empty organisation to the first entry
   * at a door. A CORE address — no workspace prefix claims `/setup` — and the one Overview's first-visit rule sends a new
   * owner to. Also reached from Overview's header and first-run panels, the first line of Settings → Organisation,
   * **its own, ninth core sidebar entry** ("Setup guide", after Settings: Kumar, 2026-10-01 ~23:30) and by bookmark.
   * `setupStep` always writes `?step=`; `person` focuses the steps about one person (the first person, their credentials,
   * the first entry).
   */
  setup: "/setup",
  setupStep: (step: CoreSetupStep, opts?: { person?: string | null }): string =>
    setupAddress("/setup", step, { key: "person", id: opts?.person }),
  kitchenSink: "/kitchen-sink",
} as const;
